import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { AxiosResponse } from "axios";
import { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import { tesaDarkRedError } from "../../../styles/colors";
import ProductApi, { CompetitorCompany } from "../../../api/services/ProductApi";
import CompetitorProductRequest from "../../../api/models/product/CompetitorProductRequest";
import useEnv from "../../../hooks/env";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import PagedResponse from "../../../api/models/PagedResponse";
import { CompetitorCompanySearchAutocomplete } from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ProductEditCreateDialog from "../ProductEditCreateDialog";
import ProductEditCreateButtons from "../ProductEditCreateButtons";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import HiddenError from "../../../models/HiddenError";
import CompetitorProductGallery from "./CompetitorProductGallery";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidProdName: {
      color: tesaDarkRedError,
    },
    marginBottom: {
      marginBottom: theme.spacing(4),
    },
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(3),
    },
  }),
);

interface CompetitorProductForm {
  adhesive: string;
  backing: string;
  color: string;
  company: CompetitorCompany | null;
  name: string;
  tesaCoverName: string;
  thickness: string;
}

interface CompetitorProductEditCreateDialogProps extends CustomDialogProps {
  initialData?: CompetitorProduct;
  readonly?: boolean;
  exitedCallback?: () => void;
  onClose: (doUpdate: boolean) => void;
  openCreateCompanyDialog: () => void;
  updateInitialData?: (product: CompetitorProduct) => void;
}

const CompetitorProductEditCreateDialog = (props: CompetitorProductEditCreateDialogProps) => {
  const classes = useStyles();
  const { exitedCallback, initialData, onClose, open, openCreateCompanyDialog, readonly, updateInitialData } = props;
  const competitorCompanySearch = useRef<HTMLDivElement & { reset: () => void }>(null);
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);
  const [invalidProductName, setInvalidProductName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyId, setCompanyId] = useState<number | null>(null);
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const dialogContext = useContext(ConfirmationDialogContext);
  const { showConfirmationDialog } = dialogContext;
  const isCreate = !initialData;

  const showGallery = open && !isCreate;

  const showExtraSaveButton = !showGallery && !readonly;
  const showGalleryAddButton = showGallery && !readonly;
  let formRef: FormikProps<CompetitorProductForm> | null = null;

  useEffect(() => {
    const interval = setTimeout(() => {
      if (searchTerm) {
        productApi
          .validateCompetitorProductName(companyId, searchTerm)
          .then((res: AxiosResponse<boolean>) => setInvalidProductName(res.data))
          .catch(handleHttpError);
      }
    }, 300);
    return () => {
      clearInterval(interval);
    };
  }, [searchTerm]);

  const yupValidationSchema = Yup.object().shape({
    company: Yup.mixed().required("Please select or create a company"),
    name: Yup.string().required("Please enter a product name"),
  });

  const formToPayload = (form: CompetitorProductForm): CompetitorProductRequest => {
    const { company, ...rest } = form;
    return {
      ...rest,
      companyId: company?.id,
      thickness: parseInt(form.thickness, 10),
    };
  };

  const initializeForm = (productData: CompetitorProduct | undefined): CompetitorProductForm => {
    return {
      company: productData?.company || null,
      name: productData?.name || "",
      color: productData?.color || "",
      thickness: productData?.thickness ? String(productData?.thickness) : "",
      tesaCoverName: productData?.tesaCoverName || "",
      backing: productData?.backing || "",
      adhesive: productData?.adhesive || "",
    };
  };

  const handleSubmit = (form: CompetitorProductForm, productId?: number) => {
    if (formRef?.dirty) {
      const payload = formToPayload(form);
      const updateOrCreate = productId
        ? () => productApi.updateCompetitorProduct(productId, payload)
        : () => productApi.createNewCompetitorProduct(payload);
      return updateOrCreate()
        .then(() => {
          setInvalidProductName(false);
          setAttachmentsChanged(false);
          showSnackbar(`Competitor product ${productId ? "updated" : "created"}`, "success");
          onClose(true);
        })
        .catch(handleHttpError);
    }
    onClose(attachmentsChanged);
    setAttachmentsChanged(false);
  };

  const handleSaveToAddAttachments = (form: CompetitorProductForm) => {
    const payload = formToPayload(form);
    return productApi.createNewCompetitorProduct(payload).then(({ data }) => {
      setInvalidProductName(false);
      showSnackbar("Competitor product saved", "success");
      updateInitialData?.(data as CompetitorProduct);
    });
  };

  return (
    <Formik<CompetitorProductForm>
      enableReinitialize
      initialValues={initializeForm(initialData)}
      innerRef={(ref) => {
        if (ref) {
          formRef = ref;
        }
      }}
      onSubmit={(values) => {
        handleSubmit(values, initialData?.id);
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<CompetitorProductForm>): JSX.Element => {
        const { errors, handleBlur, handleChange, isSubmitting, resetForm, touched, values, setFieldValue } =
          formikProps;
        return (
          <ProductEditCreateDialog
            create={isCreate}
            open={open}
            title="competitor product"
            handleClose={() => {
              if (attachmentsChanged) {
                onClose(true);
                setAttachmentsChanged(false);
              } else {
                onClose(false);
              }
            }}
            handleExited={() => {
              resetForm();
              setInvalidProductName(false);
              exitedCallback?.();
            }}
          >
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={6}>
                  <CompetitorCompanySearchAutocomplete
                    autoFocus
                    disabled={readonly}
                    mandatory
                    ref={competitorCompanySearch}
                    inputLabel="Company"
                    optionFetch={(query: string): Promise<PagedResponse<CompetitorCompany>> => {
                      return productApi
                        .getCompetitorCompanies(query)
                        .then((res: AxiosResponse) => {
                          return res?.data;
                        })
                        .catch(handleHttpError);
                    }}
                    onChange={(e, value): void => {
                      resetForm({ values: undefined, errors: undefined });
                      setInvalidProductName(false);
                      setFieldValue("company", value);
                    }}
                    map={(value): { key: string; label: string } => ({
                      key: `${value.id}`,
                      label: `${value.name}`,
                    })}
                    value={values.company}
                    error={!!errors.company && !!touched.company}
                  />
                  <div className="helperTextLink" role="link" tabIndex={0} onClick={openCreateCompanyDialog}>
                    Not found? Create new Competitor Company
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    mandatory
                    name="name"
                    id="name"
                    label="Product name"
                    value={values.name}
                    type="text"
                    helperText={errors.name && touched.name ? errors.name : ""}
                    error={!!(errors.name && touched.name) || invalidProductName}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (values.company?.id) {
                        setCompanyId(values.company.id);
                      }
                      handleChange(e);
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (invalidProductName) {
                        setFieldValue("name", "");
                        setInvalidProductName(false);
                      }
                    }}
                  />
                  {invalidProductName && (
                    <div className={classes.invalidProdName}>
                      This product already exists in the Competitor Product Database. Please use that product or enter a
                      different name.
                    </div>
                  )}
                </Grid>
                <Grid item xs={3}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    name="color"
                    id="color"
                    label="Color"
                    value={values.color}
                    type="text"
                    helperText={errors.color && touched.color ? errors.color : ""}
                    error={!!(errors.color && touched.color)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={3}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    name="thickness"
                    id="thickness"
                    label="Thickness in µm"
                    value={values.thickness}
                    type="text"
                    helperText={errors.thickness && touched.thickness ? errors.thickness : ""}
                    error={!!(errors.thickness && touched.thickness)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    name="tesaCoverName"
                    id="tesaCoverName"
                    label="tesa Cover Name"
                    value={values.tesaCoverName}
                    type="text"
                    helperText={
                      errors.tesaCoverName && touched.tesaCoverName
                        ? errors.tesaCoverName
                        : "The name to be used in the external report."
                    }
                    error={!!(errors.tesaCoverName && touched.tesaCoverName)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    name="backing"
                    id="backing"
                    label="Backing"
                    value={values.backing}
                    type="text"
                    helperText={errors.backing && touched.backing ? errors.backing : ""}
                    error={!!(errors.backing && touched.backing)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomTextField
                    disabled={!values.company || readonly}
                    fullWidth
                    name="adhesive"
                    id="adhesive"
                    label="Adhesive"
                    value={values.adhesive}
                    type="text"
                    helperText={errors.adhesive && touched.adhesive ? errors.adhesive : ""}
                    error={!!(errors.adhesive && touched.adhesive)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {showGalleryAddButton && <GalleryUploadButton mt={3} hideIfNonEmpty />}
                {showExtraSaveButton && (
                  <Box mt={3}>
                    <GalleryButton
                      icon="UPLOAD"
                      text="Add Attachment"
                      onClick={() =>
                        showConfirmationDialog({
                          headline: "Save Product",
                          text: "You have to save the product to add attachments.",
                          confirmText: "Save",
                        })
                          .then(() => {
                            return formRef?.validateForm();
                          })
                          .then((formErrors) => {
                            if (formErrors && Object.keys(formErrors).length > 0) {
                              formRef?.submitForm();
                              throw new HiddenError("form validation failed");
                            }
                            return formRef?.values;
                          })
                          .then((formValues) => {
                            if (formValues) {
                              handleSaveToAddAttachments(formValues);
                            }
                          })
                          .catch(handleHttpError)
                      }
                    />
                  </Box>
                )}
                {showGallery && !!initialData && (
                  <CompetitorProductGallery
                    companyName={initialData.company.name}
                    editable={!readonly}
                    productId={initialData.id}
                    productName={initialData.name}
                    productAttachments={initialData.attachments}
                    onChange={() => setAttachmentsChanged(true)}
                  />
                )}
              </Grid>
              <ProductEditCreateButtons
                create={isCreate}
                readonly={readonly}
                disabledConfirm={isSubmitting}
                disabledDismiss={isSubmitting}
                handleDismissClick={() => {
                  onClose(false);
                }}
              />
            </Form>
          </ProductEditCreateDialog>
        );
      }}
    </Formik>
  );
};

export default CompetitorProductEditCreateDialog;
